/**
 * Рамка кадра: картинка, затемнение вне рамки, рамка двигается мышью/пальцем. Публикации задают
 * формат сети (он нужен предупреждению «мельче файла»), кадрирование кадра ролика — нет (№323).
 *
 * Чистый компонент по пропсам. Размер рамки — наибольший для пропорции (как `cropRect`),
 * тянется только положение; `onCommit` вызывается по отпусканию, `null` — «по центру».
 * `zoomable` (каталог, №447) — ползунок «Приблизить»: рамка той же пропорции меньше (`zoomRect`).
 * `width`/`height` — натуральные размеры оригинала; нет — берутся из загруженной `<img>`.
 */
import clsx from "clsx";
import { useRef, useState, type PointerEvent } from "react";

import { btnGhost } from "@/components/trends/ui";
import type { Aspect, Rect } from "@/lib/post";
import { cropFromRect, rectFromCrop, zoomOf, zoomRect, type Crop, type PostFormat } from "@/lib/postPlan";

const ZOOM_MAX = 2.5;

export interface CropFrameProps {
  src: string;
  width: number | null;
  height: number | null;
  aspect: Aspect;
  crop: Crop | null;
  /** Формат сети — только ради предупреждения «кадр мельче файла»; без него предупреждения нет
   *  (рамка 9:16 для ролика, №323). */
  format?: PostFormat;
  readOnly?: boolean;
  /** Ползунок «Приблизить» (1–2.5×): если вокруг модели много лишнего фона. */
  zoomable?: boolean;
  onCommit: (crop: Crop | null) => void;
}

export function CropFrame({ src, width, height, aspect, crop, format, readOnly, zoomable, onCommit }: CropFrameProps) {
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  // положение рамки во время перетаскивания (пиксели оригинала)
  const [drag, setDrag] = useState<Rect | null>(null);
  const start = useRef<{ px: number; py: number; rect: Rect } | null>(null);
  const box = useRef<HTMLDivElement>(null);

  // видимый кадр: после загрузки — размеры браузера (учитывают EXIF-поворот), до неё — из базы
  const w = natural?.w || width || 0;
  const h = natural?.h || height || 0;
  const known = w > 0 && h > 0;
  const rect = known ? (drag ?? rectFromCrop(crop, w, h, aspect)) : null;
  const movable = !readOnly && aspect !== "orig" && known;
  const small = !!format && rect !== null && (rect.w < format.width || rect.h < format.height);

  const onDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!movable || !rect) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { px: e.clientX, py: e.clientY, rect };
  };

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const s = start.current;
    const b = box.current?.getBoundingClientRect();
    if (!s || !b || !b.width || !b.height) return;
    const dx = ((e.clientX - s.px) / b.width) * w;
    const dy = ((e.clientY - s.py) / b.height) * h;
    const clamp = (v: number, hi: number) => Math.round(Math.min(Math.max(v, 0), hi));
    setDrag({
      ...s.rect,
      x: clamp(s.rect.x + dx, w - s.rect.w),
      y: clamp(s.rect.y + dy, h - s.rect.h),
    });
  };

  const onUp = () => {
    if (!start.current) return;
    start.current = null;
    if (drag) onCommit(cropFromRect(drag, w, h));
    setDrag(null);
  };

  const onCancel = () => {
    start.current = null;
    setDrag(null);
  };

  const frameStyle = rect
    ? {
        left: `${(rect.x / w) * 100}%`,
        top: `${(rect.y / h) * 100}%`,
        width: `${(rect.w / w) * 100}%`,
        height: `${(rect.h / h) * 100}%`,
      }
    : undefined;

  return (
    <div className="grid gap-2" data-annot="рамка кадра">
      <div
        ref={box}
        className={clsx(
          "relative w-full max-w-[320px] touch-none select-none overflow-hidden rounded-[4px] border border-border",
          movable && "cursor-move",
        )}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onCancel}
        title={movable ? "Перетащите рамку" : undefined}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          className="block w-full"
          onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        />
        {aspect !== "orig" && frameStyle ? (
          <div
            className="pointer-events-none absolute border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
            style={frameStyle}
          />
        ) : null}
      </div>
      {zoomable && movable ? (
        <label className="flex max-w-[320px] items-center gap-2 text-[12px] text-fg-muted">
          Приблизить
          <input
            type="range"
            min={1}
            max={ZOOM_MAX}
            step={0.05}
            value={Math.min(ZOOM_MAX, zoomOf(crop, w, h, aspect))}
            className="flex-1"
            onChange={(e) => {
              const z = Number(e.target.value);
              onCommit(z <= 1 && crop === null ? null : cropFromRect(zoomRect(crop, w, h, aspect, z), w, h));
            }}
          />
          <span className="tnum w-[38px] text-right">{zoomOf(crop, w, h, aspect).toFixed(1)}×</span>
        </label>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
        {movable ? (
          <button type="button" className={btnGhost} disabled={crop === null && !drag} onClick={() => onCommit(null)}>
            по центру
          </button>
        ) : null}
        {rect ? (
          <span className="tnum text-fg-faint">
            {rect.w}×{rect.h}
          </span>
        ) : null}
        {small && format ? (
          <span className="text-warn">
            кадр меньше {format.width}×{format.height}, файл растянется
          </span>
        ) : null}
      </div>
    </div>
  );
}
